// ──────────────────────────────────────────────
// useSubscriptionStatus — Derived subscription state from authStore
// ──────────────────────────────────────────────

import { useMemo } from 'react';
import { useAuthStore } from '@/stores/authStore';

interface SubscriptionStatus {
  /** True when trial expired AND no active subscription → writes blocked */
  isReadOnly: boolean;
  /** True when in free trial period (no subscription yet) */
  isTrialing: boolean;
  /** Days remaining in trial (0 if subscribed or expired) */
  trialDaysRemaining: number;
  /** Subscription status from Stripe (null if no subscription) */
  subscriptionStatus: string | null;
  /** True when subscription is scheduled to cancel (cancel_at is set) */
  isCancelling: boolean;
  /** ISO date when subscription will be cancelled (null if not cancelling) */
  cancelAt: string | null;
  /** Days until subscription cancels (0 if not cancelling) */
  cancelDaysRemaining: number;
}

export function useSubscriptionStatus(): SubscriptionStatus {
  const org = useAuthStore((s) => s.org);

  return useMemo(() => {
    if (!org) {
      return {
        isReadOnly: false, isTrialing: false, trialDaysRemaining: 0,
        subscriptionStatus: null, isCancelling: false, cancelAt: null, cancelDaysRemaining: 0,
      };
    }

    const subStatus = org.subscriptionStatus;
    const hasActiveSub = subStatus === 'active' || subStatus === 'trialing';

    // Calculate trial days remaining
    let trialDaysRemaining = 0;
    let trialActive = false;

    if (org.trialEndsAt) {
      const trialEnd = new Date(org.trialEndsAt);
      const now = new Date();
      const diffMs = trialEnd.getTime() - now.getTime();
      trialDaysRemaining = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
      trialActive = diffMs > 0;
    }

    // Calculate cancel info
    const cancelAt = org.subscriptionCancelAt ?? null;
    let cancelDaysRemaining = 0;
    const isCancelling = !!cancelAt;

    if (cancelAt) {
      const cancelDate = new Date(cancelAt);
      const now = new Date();
      const diffMs = cancelDate.getTime() - now.getTime();
      cancelDaysRemaining = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
    }

    const isTrialing = !hasActiveSub && trialActive;
    const isReadOnly = !hasActiveSub && !trialActive;

    return {
      isReadOnly,
      isTrialing,
      trialDaysRemaining: hasActiveSub ? 0 : trialDaysRemaining,
      subscriptionStatus: subStatus,
      isCancelling,
      cancelAt,
      cancelDaysRemaining,
    };
  }, [org]);
}
